import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useSettings } from '@/api/queries';
import { useSession } from '@/session/SessionContext';
import { CONFIG_TABS, resolveTab, upgradedParams } from './configTabs';

/** The tabs the caller may see, and the one `?tab=` opens (old tab and `section=` links redirect). */
export function useConfigurationTabs() {
  const { can } = useSession();
  const canSettings = can('settings.view');
  const settings = useSettings({ enabled: canSettings });
  const tabsShown = CONFIG_TABS.filter((t) => can(...t.permissions));
  const [params, setParams] = useSearchParams();
  const active = params.get('tab');
  const setActive = (key: string) => setParams({ tab: key }, { replace: true });
  const upgraded = upgradedParams(params)?.toString();
  useEffect(() => {
    if (upgraded !== undefined) setParams(new URLSearchParams(upgraded), { replace: true });
  }, [upgraded, setParams]);

  const sections = canSettings ? (settings.data?.sections ?? []) : [];
  const awaitingSection = canSettings && Boolean(active) && !settings.data;
  const { screen, current } = resolveTab(tabsShown, sections, active, awaitingSection);
  const isActive = (key: string) => (screen ? screen.key === key : current?.key === key);

  return { settings, tabsShown, sections, screen, current, isActive, setActive };
}

export type ConfigurationTabs = ReturnType<typeof useConfigurationTabs>;
